import { gql, useQuery } from "@apollo/client";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import type { IBlogsQuery } from "@repo/shopify/queries/blog-gql";
import { blogsQuery } from "@repo/shopify/queries/blog-gql";

import { NOTIFICATIONS } from "@const/notifications";

import { useLayoutStore } from "@store/layout";

interface IUseFeaturesOptions {
  blogHandle?: string;
  articlesFirst?: number;
  articlesQuery?: string;
}

export default function useFeatures(
  loaderEl?: React.RefObject<HTMLDivElement>,
  { blogHandle, articlesFirst = 250, articlesQuery }: IUseFeaturesOptions = {},
) {
  const { search } = useLocation();

  const { openToast } = useLayoutStore();

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const variables = useMemo(
    () => ({
      first: 250,
      reverse: true,
      // blogHandle 이 있으면 그 blog 만 받는다
      query: blogHandle ? `handle:${blogHandle}` : undefined,
      articles_first: articlesFirst,
      articles_sortKey: "PUBLISHED_AT",
      articles_reverse: true,
      // 빈 문자열을 보내면 빈 검색어로 필터링돼 버리므로 없을 땐 아예 안 보낸다
      articles_query: articlesQuery || undefined,
    }),
    [blogHandle, articlesFirst, articlesQuery],
  );

  const { data, loading, fetchMore } = useQuery<IBlogsQuery>(gql(blogsQuery.query), {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-first",
    onCompleted: () => {
      setIsFetching(false);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** blogHandle 이 있으면 그 blog 의 아티클, 없으면 전체 blog 를 합쳐 최신순 */
  const articles = useMemo(() => {
    const merged = data?.blogs?.edges?.flatMap(({ node }) => node.articles?.edges || []) || [];

    // 단일 blog 는 서버가 이미 PUBLISHED_AT 순으로 주므로 그대로 쓴다
    if (blogHandle) return merged;

    return [...merged].sort((prev, curr) => new Date(curr.node.publishedAt).getTime() - new Date(prev.node.publishedAt).getTime());
  }, [data?.blogs?.edges, blogHandle]);

  /** blog 중 하나라도 아티클이 더 남아 있으면 true */
  const hasNextPage = useMemo(
    () => data?.blogs?.edges?.some(({ node }) => node.articles?.pageInfo?.hasNextPage) || false,
    [data?.blogs?.edges],
  );

  /** blogs 커넥션 자체의 다음 페이지 (blog 가 250개를 넘을 때만 의미가 있다) */
  const hasMoreBlogs = data?.blogs?.pageInfo?.hasNextPage || false;
  const blogsEndCursor = data?.blogs?.pageInfo?.endCursor;

  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];

      if (target.isIntersecting && hasMoreBlogs) {
        setIsFetching(true);

        await fetchMore({
          variables: { ...variables, after: blogsEndCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              blogs: {
                ...fetchMoreResult.blogs,
                edges: [...prev.blogs.edges, ...fetchMoreResult.blogs.edges],
              },
            };
          },
        });
      }
    },
    [blogsEndCursor, fetchMore, hasMoreBlogs, variables],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "1000px 0px 0px 0px",
      threshold: 0.1,
    });

    if (loaderEl?.current) observer.observe(loaderEl.current);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, loaderEl]);

  return { articles, isFetching, loading, hasNextPage, search, blogs: data?.blogs };
}
